import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { TABLES } from "./bookingEngine";

const IMG = (cap) => `/marketing/floor/t${cap}.webp`;
// Centre (x, y) and width as % of the room; widths keep every chair at the same real-world scale.
const SPOT = {
  T1: [12, 16, 19], T2: [34, 16, 19], T3: [61, 16, 24], T4: [86, 16, 24],
  T5: [24, 49, 37], T6: [72, 49, 40], T7: [25, 82, 41], T8: [73, 82, 44],
};

const Plaque = ({ tb, chosen, busy }) => (
  <span data-no-translate
    className="absolute left-1/2 top-full mt-[3px] -translate-x-1/2 rounded-[3px] px-1 py-px font-mono-lux text-[8.5px] sm:text-[9.5px] tracking-[0.12em] leading-none whitespace-nowrap transition-[background-color,color,opacity] duration-700"
    style={chosen
      ? { background: "linear-gradient(135deg,#F8E7AE,#D9B45A 55%,#A8812E)", color: "#2A1F08", fontWeight: 700, boxShadow: "0 0 0 0.5px rgba(255,244,210,.9), 0 2px 6px rgba(0,0,0,.6)" }
      : { background: "rgba(14,10,6,.82)", color: busy ? "rgba(233,206,130,.62)" : "#E9CE7A", boxShadow: "0 0 0 0.5px rgba(212,175,55,.4)" }}>
    {tb.id} · {tb.capacity}
  </span>
);

const Table = ({ tb, state }) => {
  const [x, y, w] = SPOT[tb.id], chosen = state === "chosen", busy = state === "busy";
  return (
    <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${x}%`, top: `${y}%`, width: `${w}%` }}
      data-testid={`floor-table-${tb.id}`} data-state={state}>
      {chosen && (
        <motion.div aria-hidden className="absolute -inset-[18%] rounded-[50%] pointer-events-none" data-testid="floor-chosen-glow"
          style={{ background: "radial-gradient(closest-side, rgba(242,207,107,.75), rgba(212,175,55,.28) 55%, rgba(212,175,55,0) 100%)", filter: "blur(6px)" }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }} />
      )}
      <img src={IMG(tb.capacity)} alt="" draggable={false} decoding="async"
        className="relative block w-full h-auto select-none transition-[filter] duration-700"
        style={{ filter: busy ? "brightness(.42) saturate(.35)" : chosen ? "brightness(1.08)" : "none" }} />
      <Plaque tb={tb} chosen={chosen} busy={busy} />
    </div>
  );
};

const Legend = ({ t }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-white/60">
    <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-[3px] bg-[#6b4a2e] ring-1 ring-[#D4AF37]/40" />{t("Available")}</span>
    <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-[3px] bg-[#2a2119] ring-1 ring-white/25" />{t("Booked")}</span>
    <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-[3px]" style={{ background: "linear-gradient(135deg,#F8E7AE,#C9A145)", boxShadow: "0 0 8px rgba(212,175,55,.7)" }} />{t("ARI's pick")}</span>
  </div>
);

// Top-down dining room with real tables and one chair per seat; booked tables dim, ARI's pick glows warm gold.
export const FloorPlan3D = ({ busyIds, chosenIds }) => {
  const { t } = useLang();
  return (
    <div className="relative" data-testid="booking-floor-plan">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <p className="text-[10px] font-mono-lux tracking-[0.28em] uppercase text-[#D4AF37] whitespace-nowrap">{t("Dining room · live")}</p>
        <Legend t={t} />
      </div>
      <div className="relative aspect-square rounded-xl overflow-hidden bg-[#140e08]" role="img" aria-label={t("Dining room · live")}
        style={{ backgroundImage: "url(/marketing/floor/floor.webp)", backgroundSize: "cover", backgroundPosition: "center", boxShadow: "0 0 0 1px rgba(212,175,55,.22), 0 30px 50px -28px rgba(0,0,0,.9)" }}>
        <div aria-hidden className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(75% 65% at 50% 45%, rgba(255,226,168,.06), rgba(0,0,0,.55) 100%)" }} />
        {TABLES.map((tb) => (
          <Table key={tb.id} tb={tb} state={chosenIds.includes(tb.id) ? "chosen" : busyIds.includes(tb.id) ? "busy" : "free"} />
        ))}
      </div>
    </div>
  );
};

export default FloorPlan3D;
